/**
 * Pagination Component
 *
 * Provides pagination controls with page navigation and per-page selector
 */

import { h } from 'preact';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalResults: number;
  perPage: number;
  onPageChange: (page: number) => void;
  onPerPageChange: (perPage: number) => void;
  perPageOptions?: number[];
  className?: string;
}

export function Pagination({
  currentPage,
  totalPages,
  totalResults,
  perPage,
  onPageChange,
  onPerPageChange,
  perPageOptions = [20, 50, 100],
  className = ''
}: PaginationProps) {
  // Calculate result range
  const startResult = totalResults === 0 ? 0 : (currentPage - 1) * perPage + 1;
  const endResult = Math.min(currentPage * perPage, totalResults);

  // Handler functions
  const handleFirstPage = () => onPageChange(1);
  const handlePreviousPage = () => onPageChange(Math.max(1, currentPage - 1));
  const handleNextPage = () => onPageChange(Math.min(totalPages, currentPage + 1));
  const handleLastPage = () => onPageChange(totalPages);

  const handlePerPageChange = (e: h.JSX.TargetedEvent<HTMLSelectElement>) => {
    const newPerPage = parseInt(e.currentTarget.value, 10);
    onPerPageChange(newPerPage);
    // Reset to page 1 when changing perPage
    onPageChange(1);
  };

  // Scroll to top when changing pages
  const handlePageChangeWithScroll = (page: number) => {
    onPageChange(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // If no results, show nothing
  if (totalResults === 0) {
    return null;
  }

  return (
    <div class={`flex flex-col sm:flex-row items-center justify-between gap-4 ${className}`}>
      {/* Results info */}
      <div class="text-sm text-gray-700">
        Mostrando{' '}
        <span class="font-medium">{startResult}</span>
        {' - '}
        <span class="font-medium">{endResult}</span>
        {' de '}
        <span class="font-medium">{totalResults}</span>
        {' '}
        resultado{totalResults !== 1 ? 's' : ''}
      </div>

      {/* Pagination controls */}
      <div class="flex items-center gap-4">
        {/* Per page selector */}
        <div class="flex items-center gap-2">
          <label htmlFor="perPage" class="text-sm text-gray-700">
            Por página:
          </label>
          <select
            id="perPage"
            value={perPage}
            onChange={handlePerPageChange}
            class="block px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {perPageOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        {/* Page navigation buttons */}
        <div class="flex items-center gap-1">
          {/* First page */}
          <button
            onClick={() => handlePageChangeWithScroll(1)}
            disabled={currentPage === 1}
            class="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition"
            aria-label="Primera página"
            title="Primera página"
          >
            <svg
              class="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M11 19l-7-7 7-7m8 14l-7-7 7-7"
              />
            </svg>
          </button>

          {/* Previous page */}
          <button
            onClick={() => handlePageChangeWithScroll(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            class="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition"
            aria-label="Página anterior"
            title="Página anterior"
          >
            <svg
              class="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>

          {/* Current page indicator */}
          <div class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md">
            Página {currentPage} de {totalPages}
          </div>

          {/* Next page */}
          <button
            onClick={() => handlePageChangeWithScroll(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            class="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition"
            aria-label="Página siguiente"
            title="Página siguiente"
          >
            <svg
              class="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>

          {/* Last page */}
          <button
            onClick={() => handlePageChangeWithScroll(totalPages)}
            disabled={currentPage === totalPages}
            class="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition"
            aria-label="Última página"
            title="Última página"
          >
            <svg
              class="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M13 5l7 7-7 7M5 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
